import { useState, useCallback, ReactNode, useRef } from "react";
import GlobalLoaderContext, { TimeoutOptions } from "@/contexts/GlobalLoaderContext";
import { toast } from "@/ui/Toast/use-toast";
import GlobalLoader from "@/ui/GlobalLoader";

interface GlobalLoaderProviderProps {
  children: ReactNode;
}

const GlobalLoaderProvider = ({ children }: GlobalLoaderProviderProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const show = useCallback(
    (options?: { timeoutOptions?: TimeoutOptions }) => {
      clearExistingTimeout();
      setIsVisible(true);
      if (options?.timeoutOptions?.time) {
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          timeoutRef.current = null;
          toast({
            variant: "destructive",
            title: options?.timeoutOptions?.message || "Something went wrong",
          });
        }, options.timeoutOptions.time * 1000);
      }
    },
    [clearExistingTimeout],
  );

  const hide = useCallback(() => {
    clearExistingTimeout();
    setIsVisible(false);
  }, [clearExistingTimeout]);

  const value = {
    isVisible,
    show,
    hide,
  };

  return (
    <GlobalLoaderContext.Provider value={value}>
      {children}
      <GlobalLoader isVisible={isVisible} />
    </GlobalLoaderContext.Provider>
  );
};

export default GlobalLoaderProvider;
